import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useUI } from "@/context/UIContext";
import { useGoTo } from "./Header";
import { LaptopFrame, PhoneFrame, ScaledCanvas } from "./Devices";
import { SITES } from "./minisites";

const LINES = [["Ihr", "Unternehmen"], ["verdient", "einen", "starken"], ["digitalen", "Auftritt."]];

function Headline() {
  return (
    <h1 className="font-display text-[42px] font-semibold leading-[1.02] tracking-[-0.035em] text-ink sm:text-6xl lg:text-[76px]" data-testid="hero-heading">
      {LINES.map((l, i) => (
        <span key={i} className="mask-line">
          <span style={{ animationDelay: `${0.08 + i * 0.12}s` }}>
            {i === 2 ? (<><span className="font-serif text-[1.08em] font-normal italic tracking-[-0.01em]">digitalen</span> Auftritt.</>) : l.join(" ")}
          </span>
        </span>
      ))}
    </h1>
  );
}

function Devices() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const tilt = useTransform(scrollYProgress, [0.2, 0.6], [10, 0]);
  const phoneY = useTransform(scrollYProgress, [0.2, 0.9], [40, -50]);
  const Arch = SITES.architektur;
  const Rest = SITES.restaurant;
  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[680px] pb-10 [perspective:1600px] lg:max-w-none" aria-hidden="true">
      <motion.div style={{ rotateX: tilt, transformOrigin: "50% 100%" }} className="fade-up w-[92%]" >
        <LaptopFrame>
          <div className="pointer-events-none h-full overflow-hidden">
            <ScaledCanvas base={1280}><Arch wide /></ScaledCanvas>
          </div>
        </LaptopFrame>
      </motion.div>
      <motion.div style={{ y: phoneY }} className="absolute bottom-0 right-0 w-[30%] min-w-[118px] max-w-[210px]">
        <div className="fade-up" style={{ animationDelay: ".35s" }}>
          <PhoneFrame>
            <div className="pointer-events-none h-full">
              <ScaledCanvas base={380}><Rest /></ScaledCanvas>
            </div>
          </PhoneFrame>
        </div>
      </motion.div>
      <span className="eyebrow absolute -bottom-2 left-0 text-[11px] text-mute">Fiktive Designstudien aus diesem Projekt</span>
    </div>
  );
}

export default function Hero() {
  const { openDemo } = useUI();
  const goTo = useGoTo();
  return (
    <section className="relative overflow-hidden pt-[104px] sm:pt-[128px]" data-testid="hero-section">
      <div className="wrap grid items-center gap-12 pb-16 lg:grid-cols-[1.05fr_1fr] lg:gap-10 lg:pb-24">
        <div>
          <p className="eyebrow fade-up flex items-center gap-3 text-gold-deep">
            <span className="inline-block h-2 w-2 rounded-full bg-gold" />
            Webdesign aus Wien. Für Unternehmen im gesamten DACH-Raum.
          </p>
          <div className="mt-6"><Headline /></div>
          <p className="fade-up mt-7 max-w-[560px] text-base leading-relaxed text-mute sm:text-lg" style={{ animationDelay: ".45s" }}>
            Ob neue Website oder kompletter Neustart: Erleben Sie mit Ihrer kostenlosen Demo innerhalb von 24 Stunden, wie Ihr Unternehmen online auftreten kann.
          </p>
          <div className="fade-up mt-9 flex flex-col gap-3 sm:flex-row" style={{ animationDelay: ".55s" }}>
            <button type="button" data-testid="hero-demo-cta" onClick={() => openDemo()} className="btn btn-ink group !min-h-[54px] !px-7">
              Meine kostenlose Demo anfordern
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button type="button" data-testid="hero-examples-cta" onClick={() => goTo("beispiele")} className="btn btn-line !min-h-[54px] !px-7">
              Website-Beispiele entdecken <ArrowDown className="h-4 w-4" />
            </button>
          </div>
          <p className="fade-up mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] font-medium text-ink/80" style={{ animationDelay: ".65s" }} data-testid="hero-offer-note">
            <span>Kostenlose Demo</span><span className="text-gold">·</span><span>Innerhalb von 24 Stunden</span><span className="text-gold">·</span><span>Auch am Wochenende</span>
          </p>
        </div>
        <Devices />
      </div>
    </section>
  );
}
